// Renders a Shopify id as a link into Shopify admin, opened in a new tab.
//
// The store handle is derived from a Shop domain — the customer's own field12
// ("juiceplus-eur.myshopify.com" → "juiceplus-eur"), or an order's other20
// (ShopName). Links are
//   https://admin.shopify.com/store/<handle>/customers/<id>
//   https://admin.shopify.com/store/<handle>/orders/<id>
// Falls back to plain text when the id or shop domain is missing.

/** "https://juiceplus-eur.myshopify.com/foo" → "juiceplus-eur" */
function shopifyHandle(shopDomain: string | null | undefined): string | undefined {
  const handle = shopDomain
    ?.trim()
    .replace(/^https?:\/\//i, '')       // drop protocol if present
    .replace(/\/.*$/, '')               // drop any path
    .replace(/\.myshopify\.com$/i, ''); // "<handle>.myshopify.com" → "<handle>"
  return handle || undefined;
}

function ShopifyLink({
  id, shopDomain, resource, title, className,
}: {
  id:         string | null | undefined;
  shopDomain: string | null | undefined;
  resource:   'customers' | 'orders';
  title:      string;
  className?: string;
}) {
  if (!id) return null;
  const handle = shopifyHandle(shopDomain);
  if (!handle) return <span className={className}>{id}</span>;
  return (
    <a
      href={`https://admin.shopify.com/store/${handle}/${resource}/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={title}
      className={`${className ?? ''} text-brand-700 hover:underline`.trim()}
    >
      {id}
    </a>
  );
}

export function ShopifyCustomerLink(props: {
  id:         string | null | undefined;
  shopDomain: string | null | undefined;
  className?: string;
}) {
  return <ShopifyLink {...props} resource="customers" title="Open in Shopify admin" />;
}

/**
 * Order-side variant (#859). `id` is the Shopify order id (other16) or order
 * number (other19); `shopDomain` is the order's ShopName (other20).
 */
export function ShopifyOrderLink(props: {
  id:         string | null | undefined;
  shopDomain: string | null | undefined;
  className?: string;
}) {
  return <ShopifyLink {...props} resource="orders" title="Open order in Shopify admin" />;
}
